import * as Sentry from '@sentry/react';

/**
 * Wraps any function with Sentry performance tracking
 * @param fn The function to track
 * @param operationName A descriptive name for the operation
 * @param operationType The type of operation
 * @param metadata Optional metadata to attach to the span
 * @returns A wrapped function with the same signature as the original
 */
export function trackOperation<T extends (...args: any[]) => any>(
  fn: T,
  operationName: string,
  operationType: string,
  metadata?: Record<string, any>
): (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>> {
  return (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
    return Sentry.startSpan(
      {
        name: operationName,
        op: operationType,
        attributes: metadata || {},
      },
      async (span) => {
        try {
          const result = await fn(...args);
          return result;
        } catch (error) {
          span.setStatus({
            code: 2, // ERROR
            message: error instanceof Error ? error.message : String(error),
          });

          throw error;
        }
      }
    );
  };
}
